import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FAQ_BY_PATH, FaqItem } from './faqData';

type SeoEntry = {
  title: string;
  description: string;
  robots?: string;
  /** Absolute path this route should canonicalise to. Set it on alias routes
   *  (e.g. /pentities -> /solutions/pentities) so duplicates consolidate. */
  canonicalPath?: string;
  /** Label used for this page in the BreadcrumbList trail. */
  breadcrumb?: string;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

const SITE_NAME = 'Meta-365';
const SITE_URL = 'https://www.meta-365.com';
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const SOCIAL_IMAGE = `${SITE_URL}/logo512.png`;

const DEFAULT_SEO: SeoEntry = {
  title: 'Meta-365 | Microsoft 365 Automation & Productivity Solutions',
  description:
    'Meta-365 delivers Microsoft 365 automation, AI-powered workflows, document intelligence, and productivity solutions for modern teams.',
  robots: 'index, follow',
};

/** Meta-365 sells per-tenant, not per-seat, and the entry tier is free. AggregateOffer
 *  states that honestly without asserting a single price the pricing page contradicts.
 *  Declared before SEO_BY_PATH because the entries below reference it at module load. */
const PLATFORM_OFFER = {
  '@type': 'AggregateOffer',
  priceCurrency: 'USD',
  lowPrice: '0',
  offerCount: '4',
  url: `${SITE_URL}/plans-pricing`,
};

const SEO_BY_PATH: Record<string, SeoEntry> = {
  '/': {
    title: 'Meta-365 | Microsoft 365 Automation & AI Productivity Platform',
    description:
      'Transform operations with Meta-365 solutions for Microsoft 365 and Azure, including AI automation, document intelligence, and workflow optimization.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Meta-365 Home',
      description:
        'Transform operations with Meta-365 solutions for Microsoft 365 and Azure, including AI automation, document intelligence, and workflow optimization.',
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
      primaryImageOfPage: `${SITE_URL}/logo192.png`,
    },
  },
  '/about': {
    breadcrumb: 'About',
    title: 'About Meta-365 | Microsoft 365 Digital Workplace Experts',
    description:
      'Learn about Meta-365 and our enterprise-grade expertise in Microsoft 365 solutions, automation, and digital workplace transformation.',
    robots: 'index, follow',
  },
  '/get-started': {
    breadcrumb: 'Get Started',
    title: 'Get Started | Deploy Meta-365 Solutions Faster',
    description:
      'Start your Meta-365 journey with implementation steps, onboarding guidance, and support to launch Microsoft 365 automation quickly.',
    robots: 'index, follow',
  },
  '/contactus': {
    breadcrumb: 'Contact',
    title: 'Contact Meta-365 | Talk to Our Team',
    description:
      'Contact Meta-365 for product questions, implementation guidance, and support for Microsoft 365 automation and productivity solutions.',
    robots: 'index, follow',
  },
  '/schedule': {
    breadcrumb: 'Book a Demo',
    title: 'Schedule a Demo | See Meta-365 in Action',
    description:
      'Book a personalized Meta-365 demo to explore AI automation, document workflows, and Microsoft 365 productivity solutions for your business.',
    robots: 'index, follow',
  },
  '/the-challenge': {
    breadcrumb: 'The Challenge',
    title: 'The Challenge | Enterprise Workflow Problems Meta-365 Solves',
    description:
      'The fragmented content, complex processes and governance gaps that hold Microsoft 365 teams back and how Meta-365 solves them with a single connected platform.',
    robots: 'index, follow',
  },
  // Alias of /the-challenge - same component, so it points its canonical there
  // instead of competing with it as duplicate content.
  '/casestudy': {
    breadcrumb: 'The Challenge',
    title: 'The Challenge | Enterprise Workflow Problems Meta-365 Solves',
    description:
      'The fragmented content, complex processes and governance gaps that hold Microsoft 365 teams back and how Meta-365 solves them with a single connected platform.',
    robots: 'index, follow',
    canonicalPath: '/the-challenge',
  },
  '/solutions/pentities': {
    breadcrumb: 'pEntities',
    title: 'pEntities | AI-Powered Microsoft 365 Automation',
    description:
      'Discover pEntities, an AI-powered platform for document automation, SharePoint integration, and intelligent Microsoft 365 workflow orchestration.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'pEntities',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'AI-powered Microsoft 365 automation platform for document intelligence and workflow orchestration.',
      provider: { '@id': ORG_ID },
      offers: PLATFORM_OFFER,
    },
  },
  // Alias of /solutions/pentities
  '/pentities': {
    breadcrumb: 'pEntities',
    title: 'pEntities | AI-Powered Microsoft 365 Automation',
    description:
      'Discover pEntities, an AI-powered platform for document automation, SharePoint integration, and intelligent Microsoft 365 workflow orchestration.',
    robots: 'index, follow',
    canonicalPath: '/solutions/pentities',
  },
  '/solutions/hr-hub': {
    breadcrumb: 'Employee Hub',
    title: 'Employee Hub | HR & Leave Management in Microsoft Teams',
    description:
      'Run leave and absence, onboarding, performance reviews and HR analytics inside Microsoft Teams, with employee data staying in your own Microsoft 365 tenant.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Meta-365 Employee Hub',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'HR and employee management module for Microsoft 365: leave and absence, onboarding automation, performance reviews and HR analytics inside Microsoft Teams.',
      provider: { '@id': ORG_ID },
      offers: PLATFORM_OFFER,
    },
  },
  '/solutions/ticketing': {
    breadcrumb: 'Ticketing',
    title: 'Ticketing | Microsoft Teams Helpdesk & Service Desk',
    description:
      'A Teams-native ticketing system with per-category SLA clocks, AI triage, auto-assignment and a PIN-link customer portal that needs no Microsoft 365 account.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Meta-365 Ticketing',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Microsoft Teams ticketing and service desk with SLA tracking, AI triage, escalation and an external customer portal.',
      provider: { '@id': ORG_ID },
      offers: PLATFORM_OFFER,
    },
  },
  '/solutions/finance': {
    breadcrumb: 'Finance',
    title: 'Finance | Invoice Processing & Document Intelligence',
    description:
      'Extract structured data from invoices, contracts, receipts and IDs with AI document intelligence that runs inside your own Microsoft 365 tenant.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Meta-365 Finance',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Invoice processing and AI document intelligence for Microsoft 365, extracting structured data from financial documents.',
      provider: { '@id': ORG_ID },
      offers: PLATFORM_OFFER,
    },
  },
  '/solutions/features': {
    breadcrumb: 'Platform Features',
    title: 'Platform Features | Meta-365 for Microsoft 365',
    description:
      'Explore the full Meta-365 feature set: document management, workflow designer, built-in AI, records and retention, and governance across Microsoft 365.',
    robots: 'index, follow',
  },
  '/solutions/searchassistant': {
    breadcrumb: 'Search Assistant',
    title: 'Search Assistant | Advanced SharePoint & M365 Search',
    description:
      'Search Assistant provides advanced filtering, multi-source search, document preview, and metadata management across SharePoint and Microsoft 365.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Search Assistant',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Advanced SharePoint and Microsoft 365 search with custom filters, previews, and metadata-aware retrieval.',
      provider: { '@id': ORG_ID },
      offers: PLATFORM_OFFER,
    },
  },
  '/solutions/pshareemail': {
    breadcrumb: 'PShareEmail',
    title: 'PShareEmail | Outlook to SharePoint Email Management',
    description:
      'Manage Outlook email better with PShareEmail. Save messages to SharePoint, OneDrive, or Teams and streamline collaboration in Microsoft 365.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'PShareEmail',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Outlook-to-SharePoint email management for Microsoft 365 with better organization and collaboration.',
      provider: { '@id': ORG_ID },
      offers: PLATFORM_OFFER,
    },
  },
  '/solutions/docsearch': {
    breadcrumb: 'Document Management',
    title: 'DocSearch | SharePoint Content Management & Search',
    description:
      'DocSearch centralizes search and file management across SharePoint, OneDrive, and Teams for faster retrieval and cleaner content operations.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'DocSearch',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'SharePoint, OneDrive, and Teams content search and management from one unified interface.',
      provider: { '@id': ORG_ID },
      offers: PLATFORM_OFFER,
    },
  },
  '/solutions/automation': {
    breadcrumb: 'Automation',
    title: 'Automation | AI Agents for Microsoft 365 Workflows',
    description:
      'Automate repetitive work with AI agents and intelligent workflow orchestration while keeping your existing Microsoft 365 systems intact.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Meta-365 Automation Services',
      serviceType: 'AI workflow automation for Microsoft 365',
      description:
        'AI agents and workflow orchestration services that automate repetitive Microsoft 365 business operations.',
      provider: { '@id': ORG_ID },
      areaServed: 'Global',
      url: `${SITE_URL}/solutions/automation`,
    },
  },
  '/solutions/hr-template': {
    breadcrumb: 'HR Operations',
    title: 'HR Template | Digital HR Operations in Microsoft 365',
    description:
      'Use the pEntities HR Template to centralize employee documents, approvals, and communications across Outlook, Teams, and SharePoint.',
    robots: 'index, follow',
  },
  '/solutions/case-management': {
    breadcrumb: 'Legal Case Management',
    title: 'Case Management | Legal Operations in Microsoft 365',
    description:
      'Run legal case management, compliance records, and contract workflows in one Microsoft 365 environment using the pEntities legal template.',
    robots: 'index, follow',
  },
  '/solutions/metadata': {
    breadcrumb: 'Metadata',
    title: 'Metadata in Microsoft 365 | Better Content Governance',
    description:
      'Learn how metadata improves Microsoft 365 search, governance, compliance, and workflow automation for enterprise content management.',
    robots: 'index, follow',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Meta-365',
    description:
      'Read the Meta-365 Privacy Policy, including how personal information is collected, used, and protected when using our services.',
    robots: 'noindex, follow',
  },
  '/terms-of-use': {
    title: 'Terms of Use | Meta-365',
    description:
      'Review Meta-365 Terms of Use and End User License Agreement governing access to products and services.',
    robots: 'noindex, follow',
  },
  '/knowledge': {
    breadcrumb: 'Knowledge Base',
    title: 'Product Knowledge Base | Meta-365',
    description:
      'Browse the Meta-365 knowledge base for detailed product information on pEntities, PShareEmail, Search Assistant, and DocSearch.',
    robots: 'index, follow',
  },
  '/technical-support': {
    title: 'Technical Support | Meta-365',
    description:
      'Get technical support for Meta-365 Microsoft Teams applications by submitting issue details through our support workflow.',
    robots: 'noindex, follow',
  },
  '/plans-pricing': {
    breadcrumb: 'Plans & Pricing',
    title: 'Plans & Pricing | Meta-365',
    description:
      'Compare Meta-365 plans and pricing to choose the right Microsoft 365 automation package for your team.',
    robots: 'index, follow',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Meta-365 Plans and Pricing',
      description:
        'Plans and pricing for Meta-365 Microsoft 365 automation products and services.',
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
    },
  },
  // Internal / work-in-progress routes: reachable, but kept out of the index.
  '/mainpage2': {
    title: 'Meta-365',
    description: 'Internal preview page.',
    robots: 'noindex, nofollow',
  },
  '/bioland': {
    title: 'Meta-365',
    description: 'Internal preview page.',
    robots: 'noindex, nofollow',
  },
};

function buildFaqJsonLd(faq: FaqItem[], pageUrl: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

/** Derive the breadcrumb trail from the URL segments, e.g.
 *  /solutions/hr-hub -> Home > Solutions > Employee Hub */
function buildBreadcrumbJsonLd(path: string, label: string | undefined): Record<string, unknown> | null {
  if (path === '/') return null;

  const segments = path.split('/').filter(Boolean);
  const items: Array<Record<string, unknown>> = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
  ];

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const url = `${SITE_URL}/${segments.slice(0, index + 1).join('/')}`;
    const name = isLast
      ? label ?? titleCase(segment)
      : titleCase(segment);
    items.push({ '@type': 'ListItem', position: index + 2, name, item: url });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function titleCase(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getGlobalJsonLd(pageUrl: string): Array<Record<string, unknown>> {

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': ORG_ID,
      name: SITE_NAME,
      url: SITE_URL,
      email: 'info@meta-365.com',
      logo: `${SITE_URL}/logo192.png`,
      // Entity consolidation: helps Google/Bing/Copilot tie the site, the social
      // profiles and the marketplace listings to one vendor.
      sameAs: [
        'https://www.linkedin.com/company/meta-365',
        'https://www.facebook.com/profile.php?id=61589941284929',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      name: SITE_NAME,
      url: SITE_URL,
      publisher: { '@id': ORG_ID },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${SITE_URL}/knowledge?query={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: document.title,
      description: document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '',
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
    },
  ];
}

function setOrCreateJsonLd(id: string, payload: Record<string, unknown> | Array<Record<string, unknown>>): void {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.text = JSON.stringify(payload);
}

function removeJsonLd(id: string): void {
  const script = document.getElementById(id);
  if (script) {
    script.remove();
  }
}

function setOrCreateMetaByName(name: string, content: string): void {
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setOrCreateMetaByProperty(property: string, content: string): void {
  let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setOrCreateCanonical(href: string): void {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    // Normalise a trailing slash so /about/ and /about resolve to one entry.
    const path =
      location.pathname.length > 1 && location.pathname.endsWith('/')
        ? location.pathname.slice(0, -1)
        : location.pathname;

    const known = SEO_BY_PATH[path];
    const seo = known ?? DEFAULT_SEO;

    // Azure Static Web Apps rewrites unmatched URLs to index.html with a 200, so an
    // unknown path would otherwise be indexable as a soft 404. Any route without its
    // own SEO_BY_PATH entry is treated as one and kept out of the index.
    const robots = known ? seo.robots ?? DEFAULT_SEO.robots ?? 'index, follow' : 'noindex, follow';

    const canonicalUrl = `${SITE_URL}${seo.canonicalPath ?? path}`;

    document.title = seo.title;
    setOrCreateMetaByName('description', seo.description);
    setOrCreateMetaByName('robots', robots);

    setOrCreateMetaByProperty('og:site_name', SITE_NAME);
    setOrCreateMetaByProperty('og:locale', 'en_GB');
    setOrCreateMetaByProperty('og:title', seo.title);
    setOrCreateMetaByProperty('og:description', seo.description);
    setOrCreateMetaByProperty('og:type', 'website');
    setOrCreateMetaByProperty('og:url', canonicalUrl);
    setOrCreateMetaByProperty('og:image', SOCIAL_IMAGE);
    setOrCreateMetaByProperty('og:image:alt', SITE_NAME);

    setOrCreateMetaByName('twitter:card', 'summary_large_image');
    setOrCreateMetaByName('twitter:title', seo.title);
    setOrCreateMetaByName('twitter:description', seo.description);
    setOrCreateMetaByName('twitter:image', SOCIAL_IMAGE);

    setOrCreateCanonical(canonicalUrl);

    const globalJsonLd = getGlobalJsonLd(canonicalUrl);
    setOrCreateJsonLd('jsonld-global', globalJsonLd);

    if (seo.jsonLd) {
      setOrCreateJsonLd('jsonld-page', seo.jsonLd);
    } else {
      removeJsonLd('jsonld-page');
    }

    const breadcrumb = buildBreadcrumbJsonLd(seo.canonicalPath ?? path, seo.breadcrumb);
    if (breadcrumb) {
      setOrCreateJsonLd('jsonld-breadcrumb', breadcrumb);
    } else {
      removeJsonLd('jsonld-breadcrumb');
    }

    // FAQ schema is driven by the same data the visible <FaqSection> renders, so the
    // markup can never claim questions the page does not actually show.
    const faq = FAQ_BY_PATH[seo.canonicalPath ?? path];
    if (faq && faq.length > 0) {
      setOrCreateJsonLd('jsonld-faq', buildFaqJsonLd(faq, canonicalUrl));
    } else {
      removeJsonLd('jsonld-faq');
    }
  }, [location.pathname]);

  return null;
}

